/* import __COLOCATED_TEMPLATE__ from './select.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import ENV from 'embercom/config/environment';
import type { EntityType } from 'embercom/models/data/entity-types';
import { isEmpty } from '@ember/utils';
// @ts-ignore
import { createDropdownGroupFromGroupList } from '@intercom/pulse/lib/dropdown-group';
import type IntlService from 'ember-intl/services/intl';
import { objectIcons } from 'embercom/models/data/matching-system/matching-constants';

interface ContentOption {
  text: string;
  value: string;
  entityType: string;
  isSelected?: boolean;
}

export type EntityTupleWithTitle = {
  entityId: number;
  entityType: EntityType;
  title: string;
};

interface Args {
  isLoading: boolean;
  contentEntities: Array<EntityTupleWithTitle>;
  isRelatedContentSearch: boolean;
  onContentClick: (entityId: number, entityType: EntityType) => void;
  onSearchTermChange: (searchTerm: string) => void;
  onClose: () => void;
  onOpen: () => void;
}

type ContentSelectSignature = {
  Args: Args;
  Element: HTMLDivElement;
  Blocks: {
    default: [];
  };
};

export default class Select extends Component<ContentSelectSignature> {
  @service declare appService: any;
  @service declare intl: IntlService;

  get dropdownGroups() {
    if (this.args.isRelatedContentSearch) {
      return this.recommendedContentGroups;
    } else {
      return this.searchResultGroups;
    }
  }

  get groupTree() {
    return createDropdownGroupFromGroupList(this.dropdownGroups);
  }

  get recommendedContentGroups() {
    if (isEmpty(this.args.contentEntities)) {
      return [];
    }
    return [
      {
        heading: this.intl.t('knowledge-hub.content-select.recommended'),
        items: this.toDropdownItems(this.args.contentEntities),
      },
    ];
  }

  toDropdownItems(entityTuples: EntityTupleWithTitle[]) {
    if (isEmpty(entityTuples)) {
      return [];
    }
    return entityTuples.map((result) => ({
      text: result.title,
      value: `${result.entityType}:${result.entityId}`,
      icon: objectIcons[result.entityType],
    }));
  }

  get searchResultGroups() {
    if (isEmpty(this.args.contentEntities)) {
      return [];
    }
    return [
      {
        items: this.toDropdownItems(this.args.contentEntities),
      },
    ];
  }

  @action
  onSearchTermChange(value: string) {
    debounce(this, this.debouncedSearch, value, ENV.APP._500MS);
  }

  @action
  debouncedSearch(value: string) {
    this.args.onSearchTermChange(value);
  }

  @action
  selectItem(item: ContentOption) {
    let [entityType, entityId] = item.value.split(':');
    this.args.onContentClick(Number(entityId), Number(entityType));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentSelect::Select': typeof Select;
    'knowledge-hub/content-select/select': typeof Select;
  }
}
